import { twMerge } from 'tailwind-merge';

export type UtilizationMeterProps = {
  /** Current usage. */
  value: number;
  /** Effective limit. Pass `null`/`undefined` to render an empty track. */
  max?: number | null;
  /**
   * `primary` renders a brand-colored bar,
   * `status` colors the bar by utilization (green/amber/red).
   */
  tone?: 'primary' | 'status';
  size?: 'small' | 'medium';
  className?: string;
};

export function getUtilizationRatio(
  value: number,
  max?: number | null
): number | null {
  if (!Number.isFinite(max) || Number(max) <= 0) {
    return null;
  }

  return Math.min(Math.max(value / Number(max), 0), 1);
}

/**
 * Ratios for a two-segment (this workspace vs. other workspaces) bar. The
 * other-workspaces segment is capped to whatever bar space this workspace
 * leaves, so the segments never overflow the track.
 */
export function getStackedUtilizationRatios(
  value: number,
  otherValue: number,
  max: number
): { ownRatio: number; otherRatio: number } {
  const ownRatio = getUtilizationRatio(value, max) ?? 0;
  const otherRatio = Math.min(
    getUtilizationRatio(otherValue, max) ?? 0,
    1 - ownRatio
  );

  return { ownRatio, otherRatio };
}

/** 0–70% green, 70–95% amber, 95%+ red. */
function getStatusColor(ratio: number) {
  if (ratio >= 0.95) {
    return 'bg-[hsl(var(--danger-9))]';
  }

  if (ratio >= 0.7) {
    return 'bg-[hsl(var(--warning-9))]';
  }

  return 'bg-[hsl(var(--success-10))]';
}

const TRACK_CLASSES = 'overflow-hidden rounded-full bg-[hsl(var(--ui-4))]';

function getTrackHeight(size: 'small' | 'medium') {
  return size === 'small' ? 'h-1' : 'h-2';
}

/**
 * Slim utilization progress bar used by the utilization overview cards and
 * the workspace limits table.
 */
export function UtilizationMeter({
  value,
  max,
  tone = 'status',
  size = 'small',
  className,
}: UtilizationMeterProps) {
  const ratio = getUtilizationRatio(value, max);

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={Number.isFinite(max) ? Number(max) : undefined}
      aria-valuenow={Number.isFinite(max) ? value : undefined}
      className={twMerge(TRACK_CLASSES, getTrackHeight(size), className)}
    >
      {ratio !== null && (
        <div
          className={twMerge(
            'h-full rounded-full transition-[width]',
            tone === 'primary'
              ? 'bg-[hsl(var(--primary-9))]'
              : getStatusColor(ratio)
          )}
          style={{ width: `${ratio * 100}%` }}
        />
      )}
    </div>
  );
}

export type StackedUtilizationMeterProps = {
  /** Usage of the current workspace. */
  value: number;
  /** Usage of all other workspaces combined. */
  otherValue: number;
  /** Shared (organization) limit the segments are measured against. */
  max: number;
  size?: 'small' | 'medium';
  className?: string;
};

/**
 * Stacked bar showing this workspace's share of a limit pool next to the
 * usage of all other workspaces.
 */
export function StackedUtilizationMeter({
  value,
  otherValue,
  max,
  size = 'medium',
  className,
}: StackedUtilizationMeterProps) {
  const { ownRatio, otherRatio } = getStackedUtilizationRatios(
    value,
    otherValue,
    max
  );

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(value + otherValue, max)}
      className={twMerge('flex', TRACK_CLASSES, getTrackHeight(size), className)}
    >
      <div
        className="h-full bg-[hsl(var(--primary-9))]"
        style={{ width: `${ownRatio * 100}%` }}
      />
      <div
        className="h-full bg-[hsl(var(--ui-8))]"
        style={{ width: `${otherRatio * 100}%` }}
      />
    </div>
  );
}
